import { createSearchParams, useNavigate, useParams } from 'react-router-dom'

import Button from '../common/Button'
import InlineCheckbox from '../common/InlineCheckbox'
import InputText from '../common/InputText'
import TextArea from '../common/TextArea'
import { useContext, useEffect, useState } from 'react'
import { NavContext } from '../../context/global_context'
import { notifyError } from '../../utils/notifications'
import { getLocalStorageData } from '../../utils/LocalStorageAccess'
import { getRequest, postRequest, putRequest } from '../../services/base'
import SelectInput from '../common/SelectInput'
import { MAKE_ID } from '../../constants/common'
import { getSessionStorageData } from '../../utils/SessionStorage'
import { Card, Flex, Form } from 'antd'
import { useVehicles } from '../../context/vehicle_context'
import { useIntl } from 'react-intl'

const VehicleForm = (props) => {
  const intl = useIntl()
  const { vehicleDetails, setVehicleDetails } = props
  const { vehicle } = useParams()
  const [value, dispatch] = useContext(NavContext)
  const [optionCodes, setOptionCodes] = useState([])
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { selectedVehicleName } = useVehicles()
  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  useEffect(() => {
    dispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header: selectedVehicleName
          ? intl.formatMessage({ id: 'layout.vehicleList' }) + ` / ${selectedVehicleName} / ` + intl.formatMessage({ id: 'vehicleForm.editVehicle' })
          : intl.formatMessage({ id: 'layout.vehicleList' }) + ' / ' + intl.formatMessage({ id: 'vehicleForm.createNewVehicle' }),
        id: '',
        show_back_btn: true,
        dtc_details: {},
        vehicle: vehicle,
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getsetOptionCodes()
    setDetailsInState('make', getLocalStorageData(MAKE_ID))
  }, [])

  const saveVehicle = async (values) => {
    const data = { ...values }

    ;['make', 'model', 'year', 'phase'].forEach((key) => {
      if (typeof data?.[key] === 'string') {
        data[key] = data[key].toLowerCase()
      }
    })

    delete data.vin_dependent_tags

    if (typeof vehicleDetails?.vin_dependent_tags === 'object') {
      data.vin_dependent_tags = JSON.stringify(
        vehicleDetails?.vin_dependent_tags
      )
    } else {
      data.vin_dependent_tags = JSON.stringify(
        vehicleDetails?.vin_dependent_tags
          ? vehicleDetails?.vin_dependent_tags.split(',')
          : ''
      )
    }

    // convert to FormData
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })
    // console.log([...formData.entries()])

    if (vehicle) {
      let res = await putRequest(
        `/api/vehicles/update_vehicle_details/${vehicle}/`,
        formData
      )
      navigate(vehicle_redirect_url)
    } else {
      let res = await postRequest(
        `/api/vehicles/save_vehicle_details/`,
        formData
      )
      navigate(vehicle_redirect_url)
    }
  }

  const setDetailsInState = (name, value) => {
    setVehicleDetails({ ...vehicleDetails, [`${name}`]: value })
  }

  const getsetOptionCodes = async () => {
    if (vehicle) {
      let res = await getRequest(
        `/api/option_codes/options/?vehicle=${vehicle}`
      )
      if (res?.status === false) {
        notifyError(dispatch, intl.formatMessage({ id: 'generic.optionsLoadError' }))
        return
      }
      setOptionCodes(res.data || [])
    } else {
      setOptionCodes([])
    }
  }
  const getInitialValues = () => {
    return {
      make: vehicleDetails?.make,
      model: vehicleDetails?.model,
      year: vehicleDetails?.year,
      phase: vehicleDetails?.phase,
      make_description: vehicleDetails?.make_description,
      model_description: vehicleDetails?.model_description,
      default_option_code: vehicleDetails?.default_option_code?.toString(),
      tester_id: vehicleDetails?.tester_id,
      time_zone: vehicleDetails?.time_zone,
      company: vehicleDetails?.company,
      make_model_id: vehicleDetails?.make_model_id,
      model_year_sales_code: vehicleDetails?.model_year_sales_code,
      vin_dependent_tags: vehicleDetails?.vin_dependent_tags,
      svg_enabled: vehicleDetails?.svg_enabled,
      svg_stroke: vehicleDetails?.svg_stroke,
      svg_fill: vehicleDetails?.svg_fill,
      svg_resolution: vehicleDetails?.svg_resolution,
    }
  }
  useEffect(() => {
    form.setFieldsValue(getInitialValues())
  }, [vehicleDetails])

  return (
    <div className="vehicle-form">
      <Form
        id="vehicle-form-details"
        name="vehicle-form-details"
        form={form}
        layout="vertical"
        onFinish={saveVehicle}
      >
        <Card title={intl.formatMessage({ id: 'vehicleForm.basicDetails' })} variant="outlined">
          <Flex wrap gap={16} style={{ paddingBottom: 16 }}>
            <InputText
              name="make"
              type="text"
              label={intl.formatMessage({ id: 'vehicleForm.make' })}
              disabled={getLocalStorageData(MAKE_ID) ? true : false}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('make', value)
              }}
            />
            <InputText
              name="model"
              type="text"
              label={intl.formatMessage({ id: 'vehicleForm.model' })}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('model', value)
              }}
            />
            <InputText
              name="year"
              type="number"
              label={intl.formatMessage({ id: 'vehicleForm.manufacturingYear' })}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('year', value)
              }}
            />
            <InputText
              name="phase"
              type="text"
              label={intl.formatMessage({ id: 'vehicleForm.phase' })}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('phase', value)
              }}
            />
          </Flex>
          <Flex wrap gap={16} style={{ paddingBottom: 16 }}>
            <InputText
              name="make_description"
              type="text"
              label={intl.formatMessage({ id: 'vehicleForm.makeDescription' })}
              isRequired={false}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('make_description', value)
              }}
            />
            <InputText
              name="model_description"
              type="text"
              label={intl.formatMessage({ id: 'vehicleForm.modelDescription' })}
              isRequired={false}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('model_description', value)
              }}
            />
            <SelectInput
              name="default_option_code"
              label={intl.formatMessage({ id: 'vehicleForm.defaultOptionsCode' })}
              options={optionCodes}
              isRequired={false}
              wrapperClass="justify-items-evenly"
              onChange={(option) => {
                setDetailsInState('default_option_code_data', option)
                setDetailsInState('default_option_code', option?.value)
              }}
            />
            <InputText
              name="tester_id"
              type="text"
              label={intl.formatMessage({ id: 'vehicleForm.testerId' })}
              isRequired={true}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('tester_id', value)
              }}
            />
          </Flex>
        </Card>
        <Card title={intl.formatMessage({ id: 'vehicleForm.advancedDetails' })} variant="outlined">
          <Flex wrap gap={16} style={{ paddingBottom: 16 }}>
            <InputText
              name="time_zone"
              type="text"
              label={intl.formatMessage({ id: 'vehicleForm.timeZone' })}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('time_zone', value)
              }}
            />
            <InputText
              name="company"
              type="text"
              label={intl.formatMessage({ id: 'vehicleForm.companyDetails' })}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('company', value)
              }}
            />
            <InputText
              name="make_model_id"
              type="number"
              label={intl.formatMessage({ id: 'vehicleForm.makeModelId' })}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('make_model_id', value)
              }}
            />
            <InputText
              name="model_year_sales_code"
              type="text"
              label={intl.formatMessage({ id: 'vehicleForm.modelYearSalesCode' })}
              isRequired={false}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('model_year_sales_code', value)
              }}
            />
          </Flex>
          <Flex wrap gap={16} style={{ paddingBottom: 16 }}>
            <TextArea
              name="vin_dependent_tags"
              label={
                    <span>
                      {intl.formatMessage({ id: 'vehicleForm.vinDependentTags' })}{' '}
                      <span className="field-label-hint">
                        {intl.formatMessage({ id: 'vehicleForm.vinDependentTagsHint' })}
                      </span>
                    </span>
                  }
              placeholder="REAR PARK LAMPS ON"
              wrapperClass="justify-items-evenly"
              rows={1}
              isRequired={false}
              onChange={(value) => {
                setDetailsInState('vin_dependent_tags', value)
              }}
            />
          </Flex>
        </Card>
        <Card title={intl.formatMessage({ id: 'vehicleForm.vehicleSvgDetails' })} variant="outlined">
          <Flex wrap gap={16} style={{ paddingBottom: 16 }}>
            <InlineCheckbox
              name="svg_enabled"
              label={intl.formatMessage({ id: 'vehicleForm.svgAvailable' })}
              legend={intl.formatMessage({ id: 'vehicleForm.svgStatus' })}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('svg_enabled', value)
              }}
            />
            <InputText
              name="svg_stroke"
              type="text"
              label={intl.formatMessage({ id: 'vehicleForm.svgStroke' })}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('svg_stroke', value)
              }}
            />
            <InputText
              name="svg_fill"
              type="text"
              label={intl.formatMessage({ id: 'vehicleForm.svgFill' })}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('svg_fill', value)
              }}
            />
            <InputText
              name="svg_resolution"
              type="text"
              label={intl.formatMessage({ id: 'vehicleForm.svgResolution' })}
              wrapperClass="justify-items-evenly"
              onChange={(value) => {
                setDetailsInState('svg_resolution', value)
              }}
            />
          </Flex>
        </Card>
        <Button type="primary" htmlType="submit" testId="vehicle-save">
          {intl.formatMessage({ id: 'vehicleForm.saveVehicle' })}
        </Button>
      </Form>
    </div>
  )
}

export default VehicleForm
